import { useState, useEffect } from 'react';
import { Clock3, Layers, Star, Trophy } from 'lucide-react';
import apiClient from '../services/apiClient';
import PracticeSettingsService from '../services/PracticeSettingsService';
import useResultEntrance, { resultMetricDelay, resultRecordDelay, resultRevealStyle } from '../hooks/useResultEntrance';
import './SessionSummary.css';
import './ResultEntrance.css';
import { t } from '../i18n';

export function formatResultMinutes(seconds = 0) {
  const normalizedSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  return t('practiceUi.summary.minutes', undefined, { count: Math.floor(normalizedSeconds / 60) });
}

/**
 * SessionSummary component displays the results of a completed practice session.
 *
 * @param {Object} props
 * @param {Object} props.sessionResults - Results from the practice session
 * @param {number} props.sessionResults.cardsCompleted - Number of cards practiced
 * @param {Array} props.sessionResults.evaluations - Array of evaluation objects
 * @param {number} props.sessionResults.elapsedTime - Session duration in seconds
 * @param {React.ReactNode} props.actions - Action buttons rendered below the record boxes
 */
function SessionSummary({ sessionResults, actions, entranceEnabled = true }) {
  const [dailyStats, setDailyStats] = useState(null);
  const [loadingDaily, setLoadingDaily] = useState(true);
  const entrance = useResultEntrance(Boolean(sessionResults), sessionResults?.id, !entranceEnabled);

  useEffect(() => {
    const fetchDailyStats = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 500));

        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const today = new Date().toLocaleDateString('en-CA', { timeZone: tz });
        const result = await apiClient.getDailyPracticeTotal(today, tz);
        setDailyStats({
          totalSeconds: result.totalSeconds,
          totalCardsStudied: result.totalCardsStudied,
          totalRepeatCount: result.totalRepeatCount,
          totalScoreGained: result.totalScoreGained
        });
      } catch (err) {
        console.error('Failed to fetch daily stats:', err);
        setDailyStats(sessionResults?.dailyStats || null);
      } finally {
        setLoadingDaily(false);
      }
    };

    fetchDailyStats();
  }, [sessionResults]);

  if (!sessionResults) {
    return (
      <div className="session-summary">
        <div className="session-summary-empty">
          <p>{t('practiceUi.summary.empty')}</p>
        </div>
      </div>
    );
  }

  const {
    cardsCompleted,
    evaluations = [],
    elapsedTime,
    cardsStudied,
    repeatCount,
    scoreGained,
    practicedCards = [],
    infinityMode,
    batchesCompleted,
    endReason
  } = sessionResults;
  const isQueueExhausted = endReason === 'queueExhausted';

  const getCardId = (card) => card?.id ?? card?._id;
  const getEvaluationCardId = (evaluation) => evaluation?.cardId ?? evaluation?.id ?? evaluation?._id;

  const sessionGradeByCard = new Map();
  evaluations.forEach(ev => {
    const cardId = getEvaluationCardId(ev);
    if (cardId !== undefined && cardId !== null) {
      sessionGradeByCard.set(String(cardId), ev.grade);
    }
  });

  const getCardSummaryGrade = (card) => {
    const cardId = getCardId(card);
    if (cardId !== undefined && cardId !== null && sessionGradeByCard.has(String(cardId))) {
      return sessionGradeByCard.get(String(cardId));
    }

    return PracticeSettingsService.getAssessmentGrade(card) || 1;
  };

  const totalPracticedCards = practicedCards.length;
  let averageGrade = t('practiceUi.summary.unavailable');
  if (totalPracticedCards > 0) {
    const gradesSum = practicedCards.reduce((sum, card) => sum + getCardSummaryGrade(card), 0);
    averageGrade = (gradesSum / totalPracticedCards).toFixed(2);
  }

  const sessionTime = formatResultMinutes(elapsedTime);
  const todayTime = loadingDaily
    ? '...'
    : (dailyStats?.totalSeconds !== undefined ? formatResultMinutes(dailyStats.totalSeconds) : t('practiceUi.summary.unavailable'));
  const todayCards = loadingDaily
    ? '...'
    : (dailyStats?.totalCardsStudied !== undefined ? dailyStats.totalCardsStudied : t('practiceUi.summary.unavailable'));
  const todayScore = loadingDaily
    ? '...'
    : (dailyStats?.totalScoreGained !== undefined ? dailyStats.totalScoreGained : t('practiceUi.summary.unavailable'));
  const todayRepeats = loadingDaily
    ? '...'
    : (dailyStats?.totalRepeatCount !== undefined ? dailyStats.totalRepeatCount : t('practiceUi.summary.unavailable'));
  const metrics = [
    ['cards', cardsCompleted, Layers],
    ...(infinityMode && batchesCompleted ? [['batches', batchesCompleted, null]] : []),
    ['timePracticed', sessionTime, Clock3],
    ['points', scoreGained || 0, Trophy],
    ['grade', averageGrade, Star],
  ];

  return (
    <div className={`session-summary ${entrance.className}`} onAnimationStart={entrance.onAnimationStart}>
      <div className="session-summary-header result-reveal">
        <h2>{t('practiceUi.summary.title')}</h2>
        {isQueueExhausted && (
          <p className="session-summary-end-note">
            {t('practiceUi.summary.queueExhausted')}
          </p>
        )}
        {infinityMode && (
          <div className="infinity-mode-badge">
            <span className="infinity-symbol">&infin;</span>
            <span className="infinity-label">{t('practiceUi.summary.infinity')}</span>
          </div>
        )}
      </div>

      <div className="session-summary-stats">
        {metrics.map(([label, value, Icon], index) => <div key={label} className="stat-card result-reveal" style={resultRevealStyle(resultMetricDelay(index))} data-result-tone={index}>
          {Icon && <Icon className="stat-icon" size={22} strokeWidth={2.4} aria-hidden="true" />}
          <div className={`stat-value${label === 'timePracticed' ? ' stat-value-time' : ''}`}>{value}</div>
          <div className="stat-label">{t(`practiceUi.summary.${label}`)}</div>
        </div>)}
      </div>

      <div className="session-summary-time" aria-label={t('practiceUi.summary.records')}>
        <div className="record-card result-reveal" style={resultRevealStyle(resultRecordDelay(0, metrics.length))}>
          <div className="record-label">{t('practiceUi.summary.practiceTime')}</div>
          <div className="record-values">
            <span>{t('practiceUi.summary.session')}</span>
            <strong>{sessionTime}</strong>
            <span>{t('practiceUi.summary.today')}</span>
            <strong>{todayTime}</strong>
          </div>
        </div>

        <div className="record-card result-reveal" style={resultRevealStyle(resultRecordDelay(1, metrics.length))}>
          <div className="record-label">{t('practiceUi.summary.cardsStudied')}</div>
          <div className="record-values">
            <span>{t('practiceUi.summary.session')}</span>
            <strong>{cardsStudied || 0}</strong>
            <span>{t('practiceUi.summary.today')}</span>
            <strong>{todayCards}</strong>
          </div>
        </div>

        <div className="record-card result-reveal" style={resultRevealStyle(resultRecordDelay(2, metrics.length))}>
          <div className="record-label">{t('practiceUi.summary.points')}</div>
          <div className="record-values">
            <span>{t('practiceUi.summary.session')}</span>
            <strong>{scoreGained || 0}</strong>
            <span>{t('practiceUi.summary.today')}</span>
            <strong>{todayScore}</strong>
          </div>
        </div>

        <div className="record-card result-reveal" style={resultRevealStyle(resultRecordDelay(3, metrics.length))}>
          <div className="record-label">{t('practiceUi.summary.repeats')}</div>
          <div className="record-values">
            <span>{t('practiceUi.summary.session')}</span>
            <strong>{repeatCount || 0}</strong>
            <span>{t('practiceUi.summary.today')}</span>
            <strong>{todayRepeats}</strong>
          </div>
        </div>
      </div>

      {actions && (
        <div className="session-summary-actions">
          {actions}
        </div>
      )}

      {totalPracticedCards === 0 && (
        <div className="session-summary-no-evaluations result-reveal" style={resultRevealStyle(resultRecordDelay(4, metrics.length))}>
          <p>{t('practiceUi.summary.noCards')}</p>
        </div>
      )}
    </div>
  );
}

export default SessionSummary;
